import { Navigate, Route, Routes } from "react-router-dom";
import { AuthRoles, decodeToken, isLoggedIn } from "./helpers/api/authService";
import { useAppSelector, useAppDispatch } from "./redux/hooks";
import { RootState } from "./redux/store";
import { Sandbox } from "./pages/sandbox/Sandbox";
import { LoginPage } from "./pages/login/Login";
import { UserRoutes } from "./routes/UserRoutes";
import { ErrorUrlNotFound } from "./pages/error/UrlNotFound";
import { SupportRoutes } from "./routes/SupportRoutes";
import ForgotPassword from "./pages/login/ForgotPassword";
import "./assets/scss/app.scss";
import Confirm from "./pages/login/Confirm";
import ResetPassword from "./pages/login/ResetPassword";
import { useEffect } from "react";
import { getUser } from "./redux/slices/User";
import { ToastMessages } from "./components/ToastMessages";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ThemeOptions, ThemeProvider, createTheme } from '@mui/material/styles';
import { getMainColors } from "./constants/colors";
import { LicenseManager } from  'ag-grid-enterprise'
import zIndex from "@mui/material/styles/zIndex";

function App() {
	const dispatch = useAppDispatch();
	const randomNumber = useAppSelector((state: RootState) => state.login.pageRefreshCode); //this will cause app.tsx to re-render, hopefully making everything else refresh
	const decodedToken = decodeToken();
	const loggedIn = isLoggedIn();

	LicenseManager.setLicenseKey("Using_this_AG_Grid_Enterprise_key_( AG-049749 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Z Software Pty Ltd )_is_granted_a_( Single Application )_Developer_License_for_the_application_( Z Freedom )_only_for_( 1 )_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_( Z Freedom )_need_to_be_licensed___( Z Freedom )_has_been_granted_a_Deployment_License_Add-on_for_( 1 )_Production_Environment___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 26 October 2024 )____[v2]_MTcyOTg5NzIwMDAwMA==13e60effe3c61fb6050d35187144ef6e");

	useEffect(() => {
		document.title = "Z Freedom";
	}, []);

	const newTheme = (theme: ThemeOptions | undefined) => createTheme({
	  ...theme,
	  components: {
		MuiFormControl: {
			styleOverrides: {
				root: {
					"&.invalid .MuiOutlinedInput-root>.MuiOutlinedInput-notchedOutline": {
						borderColor: getMainColors().danger,
					}
				}
			}
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
				  ':hover .MuiOutlinedInput-notchedOutline': {
					borderColor: getMainColors().primary,
				  },
				  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
					  borderColor: getMainColors().primary,
				  },
				  ".invalid .MuiOutlinedInput-notchedOutline": {
					  borderColor: getMainColors().danger,
				  },
				  color: "var(--bs-link-color)",
				},
			}	
		},
		MuiTabs: {
			styleOverrides: {
				indicator: {
					backgroundColor: getMainColors().primary,
				}
			}
		},
		MuiTab: {
			styleOverrides: {
				root: {
					"&.Mui-selected": {
						color: getMainColors().primary,
					}
				}
			}
		},
		MuiButton: {
			styleOverrides: {
				root: {
					color: getMainColors().primary
				}
			}
		},		
		MuiClock: {
			styleOverrides: {
				pin: {
					backgroundColor: getMainColors().primary,
				}
			}
		},
		MuiClockPointer: {
			styleOverrides: {
				root: {
					backgroundColor: getMainColors().primary,
				},
				thumb: {
					borderColor: getMainColors().primary,
				}
			}
		},
		MuiMultiSectionDigitalClockSection: {
			styleOverrides: {
				item: {
					borderRadius: 4,
					"&.Mui-selected": {
						backgroundColor: getMainColors().primary,
						":focus": {
							backgroundColor: getMainColors().primary,
						},
						":hover": {
							backgroundColor: getMainColors().primary,
						}
					}
				}
			}
		},
		MuiPickersYear: {
			styleOverrides: {
				yearButton: {
					'&.Mui-selected': {
						backgroundColor: getMainColors().primary,
						':focus': {
							backgroundColor: getMainColors().primary,
						},
						":hover": {
							backgroundColor: getMainColors().primary,
						}
					}
				},
			}
		},
		MuiPickersMonth: {
			styleOverrides: {
				monthButton: {
					'&.Mui-selected': {
						backgroundColor: getMainColors().primary,
						':focus': {
							backgroundColor: getMainColors().primary,
						},
						":hover": {
							backgroundColor: getMainColors().primary,
						}
					}
				},
			}
		},
		MuiPickersDay: {
			styleOverrides: {
				root: {
					"&.Mui-selected": {
						backgroundColor: getMainColors().primary,
						":focus": {
							backgroundColor: getMainColors().primary,
						},
						":hover": {
							backgroundColor: getMainColors().primary,
						}
					}
				}
			}
		}},
		MuiPickersPopper: {
			styleOverrides: {
				root: {
					zIndex: 99999
				}
			}
		}
	});

	return (
		<ThemeProvider theme={newTheme(undefined)}>
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<div>
				<div hidden={true}>{randomNumber}</div>
				<Routes>
					<Route path="/login" element={<LoginPage />} />
					<Route path="/sandbox" element={<Sandbox />} />
					<Route path="/forgot-password" element={<ForgotPassword />} />
					<Route path="/confirm" element={<Confirm />} />
					<Route path="/reset-password/:resetToken" element={<ResetPassword />} />
					{loggedIn && decodedToken !== null && decodedToken.AuthRole >= AuthRoles.Support && (
						<Route element={<SupportRoutes />} path="/support/*" />
					)}

					{loggedIn && decodedToken !== null && decodedToken.AuthRole == AuthRoles.User && (
						<Route element={<UserRoutes />} path="/app/*" />
					)}
					<Route path="/" element={<Navigate to="/login" />} />
					<Route path="*" element={<ErrorUrlNotFound />} />
				</Routes>
				<ToastMessages />
			</div>
		</LocalizationProvider>
	</ThemeProvider>
	);
}

export default App;
